import { ReactNode } from 'react';
import { PrimaryButton, TimerIcon, Typography } from '@weareopenr/component-library';
import dayjs from 'dayjs';
import classNames from 'classnames';
import type { IReviewDecision } from 'src/hooks/usePublishPlans/usePublishPlans';
import styles from './ReviewAlert.module.scss';

interface IReviewAlertProps {
  icon: ReactNode;
  title: string;
  detail: string;
  actionLabel: string;
  onAction: () => void;
  variant: 'grey' | 'blue';
}

const ReviewAlert = ({ icon, title, detail, actionLabel, onAction, variant }: IReviewAlertProps) => (
  <div className={classNames(styles['review-alert'], styles[variant])} role="alert">
    <span className={styles['alert-icon']}>{icon}</span>
    <div className={styles['alert-text']}>
      <Typography size="medium" weight="bold">
        {title}
      </Typography>
      <Typography size="small" className={styles['alert-detail']}>
        {detail}
      </Typography>
    </div>
    <PrimaryButton size="small" onClick={onAction}>
      {actionLabel}
    </PrimaryButton>
  </div>
);

export const ReviewExpiredAlert = ({
  systemNames,
  onPublishAgain,
}: {
  systemNames: string;
  onPublishAgain: () => void;
}) => (
  <ReviewAlert
    icon={<TimerIcon size="medium" />}
    title="This review has expired. Publish again to get a fresh plan."
    detail={`${systemNames} may have changed since this plan was made, so it can no longer be accepted.`}
    actionLabel="Publish again"
    onAction={onPublishAgain}
    variant="grey"
  />
);

export const ReviewDecisionAlert = ({
  decision: { outcome, decidedAt },
  systemNames,
  onClose,
  onPublishAgain,
}: {
  decision: IReviewDecision;
  systemNames: string;
  onClose: () => void;
  onPublishAgain: () => void;
}) => {
  const time = dayjs(decidedAt).format('HH:mm');
  const icon = <span className={styles['decision-dot']} />;

  if (outcome === 'changed') {
    return (
      <ReviewAlert
        icon={icon}
        title={`Publish accepted at ${time}, but ${systemNames} had changed`}
        detail="No changes from the affected plans were published. Publish again to get a fresh plan."
        actionLabel="Publish again"
        onAction={onPublishAgain}
        variant="blue"
      />
    );
  }

  return (
    <ReviewAlert
      icon={icon}
      title={`Publish already ${outcome} at ${time}`}
      detail={
        outcome === 'rejected'
          ? `Nothing else is needed from you. No changes will be made in ${systemNames}.`
          : 'Nothing else is needed from you. The publish is going ahead.'
      }
      actionLabel="Close"
      onAction={onClose}
      variant="blue"
    />
  );
};
